import { authorisation } from '../authorisation';
import { getProductNamesFromModule } from '../authorisation/utils';
import { NavRoutesRegister } from '../navigation-routes';
import { NavRouteConfig, RouteConfig } from '../navigation-routes/types';
import { AccessTags, MarketTags, PageTags } from './types';
import { matchPath } from 'react-router';

/**
 * Uses authorisation to determine the current user's available subscriptions which can be passed to analytics.
 * @param modulesWhitelist A list of modules for which to return subscriptions.
 * @returns An object that contains the list of product names the current user has access to.
 */

export const getAccessTags = async (modulesWhitelist?: string[]): Promise<AccessTags> => {
  const productNames = await authorisation.getAuthorisedProductNames();

  const filteredProductNames = modulesWhitelist
    ? modulesWhitelist
        .map(module => getProductNamesFromModule(module))
        .flat()
        .filter(productName => productNames.includes(productName))
    : productNames;

  return { access: filteredProductNames.join(',') };
};

interface PageInfo {
  pageName: string;
  section: string;
  subsection: string;
  requiredModules?: string[];
}

const doesRouteMatchPath = (path: string, route: RouteConfig): boolean => {
  if (!route.pathMatch) {
    return false;
  } else if (typeof route.pathMatch === 'string') {
    return matchPath(path, route.pathMatch) !== null;
  } else {
    return route.pathMatch.test(path);
  }
};

const createEmptyPageInfo = (): PageInfo => ({
  pageName: '',
  section: '',
  subsection: '',
  requiredModules: [],
});

const createPageInfo = (route: NavRouteConfig | RouteConfig, labelHierarchy: string[]): PageInfo => {
  const [section] = labelHierarchy;
  const subsection = labelHierarchy.length === 1 ? route.label : labelHierarchy[labelHierarchy.length - 1];
  return {
    pageName: route.label,
    section: section || route.label,
    subsection: subsection || '',
    requiredModules: route.access,
  };
};

const processSubRoutes = (
  route: NavRouteConfig | RouteConfig,
  targetPath: string,
  labelHierarchy: string[]
): PageInfo => {
  if (!route.subRoutes) return createEmptyPageInfo();

  const updatedLabelHierarchy = [...labelHierarchy, route.label];
  return getPageInfo(targetPath, route.subRoutes, updatedLabelHierarchy);
};

const getPageInfo = (
  targetPath: string,
  routes: (NavRouteConfig | RouteConfig)[] = NavRoutesRegister.getInstance().getAll(),
  labelHierarchy: string[] = []
): PageInfo => {
  if (routes?.length) {
    for (const route of routes) {
      const result = processSubRoutes(route, targetPath, labelHierarchy);
      if (result.pageName !== '') return result;

      if (doesRouteMatchPath(targetPath, route)) {
        return createPageInfo(route, labelHierarchy);
      }
    }
  }

  return createEmptyPageInfo();
};

export const getPageInfoFromRouteConfig = (targetPath: string): PageInfo => getPageInfo(targetPath);

export const getPageTags = (path: string): PageTags => {
  const { pageName, section, subsection } = getPageInfo(path);

  return {
    pageName,
    section,
    subsection,
  };
};

export const getAccessTagsOrDefault = async (tags: Partial<AccessTags>): Promise<AccessTags> => {
  return tags?.access ? { access: tags.access } : await getAccessTags();
};

export const getPageTagsOrDefault = (tags: Partial<PageTags>): PageTags => {
  return tags?.pageName || tags?.section || tags?.subsection
    ? { pageName: tags.pageName ?? '', section: tags.section ?? '', subsection: tags.subsection ?? '' }
    : getPageTags(window.location.pathname);
};

export const getMarketTagsOrDefault = (tags: Partial<MarketTags>): MarketTags => {
  return {
    locationId: tags.locationId ?? '',
    itemGroup: tags.itemGroup ?? '',
  };
};
